import {
    ComponentProps,
    ComponentPropsWithoutRef,
    ForwardedRef,
    createContext,
    forwardRef,
    useContext,
    useMemo,
} from 'react';
import { Button } from '@givebutter/primitives/button';
import { maskDecimal, maskWholeNumber } from '@givebutter/util';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import {
    faArrowPointer,
    faEnvelope,
    faEnvelopeOpen,
    faGift,
    faMobile,
    faXmarkToSlot,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import { DelayBadge } from 'assets/js/pages/engage/workflows/components/DelayBadge';
import { useFindDelayNode } from 'assets/js/pages/engage/workflows/hooks/useFindDelayNode';
import { useReactFlowStore } from 'assets/js/pages/engage/workflows/utils/store';
import { truncateSmsBody } from 'assets/js/pages/engage/workflows/utils/truncateSmsBody';
import { WorkflowActionTypeEnum } from 'constants/workflows';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { twMerge } from 'tailwind-merge';
import { WorkflowActionMetrics } from 'types/domain';

interface IActionMetricsRow {
    row: WorkflowActionMetrics;
}

const ActionMetricsRowContext = createContext<IActionMetricsRow>({
    row: {} as IActionMetricsRow['row'],
});

interface ActionMetricsRowRootProps extends Collapsible.CollapsibleProps {
    row: WorkflowActionMetrics;
}
const ActionMetricsRowRoot = forwardRef(
    (
        { children, row, ...rest }: ActionMetricsRowRootProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const providerValue = useMemo(() => ({ row }), [row]);
        return (
            <ActionMetricsRowContext.Provider value={providerValue}>
                <Collapsible.Root {...rest} ref={forwardedRef}>
                    <div className="flex flex-col gap-3 border-b border-b-gray-200 p-6">
                        {children}
                    </div>
                </Collapsible.Root>
            </ActionMetricsRowContext.Provider>
        );
    },
);

interface ActionMetricsRowTitleProps extends Omit<ComponentPropsWithoutRef<'p'>, 'children'> {}
const ActionMetricsRowTitle = forwardRef(
    (
        { className, ...rest }: ActionMetricsRowTitleProps,
        forwardedRef: ForwardedRef<HTMLParagraphElement>,
    ) => {
        const {
            row: { action },
        } = useContext(ActionMetricsRowContext);
        return (
            <p
                ref={forwardedRef}
                className={twMerge('flex items-center gap-2 text-sm font-medium', className)}
                {...rest}
            >
                {action === WorkflowActionTypeEnum.SEND_EMAIL && (
                    <>
                        <FontAwesomeIcon icon={faEnvelope} className="text-green-500" />
                        Send email
                    </>
                )}

                {action === WorkflowActionTypeEnum.SEND_SMS && (
                    <>
                        <FontAwesomeIcon icon={faMobile} className="text-green-500" />
                        Send text
                    </>
                )}
            </p>
        );
    },
);

interface ActionMetricsRowDescriptionProps
    extends Omit<ComponentPropsWithoutRef<'div'>, 'children'> {}
const ActionMetricsRowDescription = forwardRef(
    (
        { className, ...rest }: ActionMetricsRowDescriptionProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const { row } = useContext(ActionMetricsRowContext);
        const { nodes } = useReactFlowStore(({ nodes }) => ({ nodes }));
        const workflowNode = nodes.find(n => n.data?.stepId === row.id);
        const delayNode = useFindDelayNode(workflowNode);
        const { type, formData } = workflowNode?.data || {};

        if (
            (type !== WorkflowActionTypeEnum.SEND_EMAIL &&
                type !== WorkflowActionTypeEnum.SEND_SMS) ||
            !formData ||
            !workflowNode
        )
            return null;

        return (
            <div
                className={twMerge('flex flex-col items-center gap-2 sm:flex-row', className)}
                ref={forwardedRef}
                {...rest}
            >
                {delayNode && <DelayBadge delayNode={delayNode} />}
                <p className="text-sm">
                    {type === WorkflowActionTypeEnum.SEND_EMAIL && formData.values.subject}
                    {type === WorkflowActionTypeEnum.SEND_SMS &&
                        truncateSmsBody(formData.values.body)}
                </p>
            </div>
        );
    },
);

const ActionMetricsRowCollapsibleTrigger = () => {
    return (
        <Collapsible.Trigger asChild>
            <Button
                variant="transparent"
                shape={'square'}
                size="sm"
                className="group"
                aria-label="Toggle row"
            >
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className="group-data-[state=open]:rotate-180"
                />
            </Button>
        </Collapsible.Trigger>
    );
};

interface ActionMetricsRowContentProps
    extends Omit<Collapsible.CollapsibleContentProps, 'children'> {}
const ActionMetricsRowContent = forwardRef(
    (props: ActionMetricsRowContentProps, forwardedRef: ForwardedRef<HTMLDivElement>) => {
        const { row } = useContext(ActionMetricsRowContext);

        return (
            <Collapsible.Content ref={forwardedRef} {...props}>
                <div className="flex gap-14 rounded-xl bg-gray-50 p-6">
                    <p className="min-w-36">
                        <span className="text-gray-500">Sent to</span>
                        <br />
                        <span className="text-gray-700">
                            <strong>{maskWholeNumber(row.message.supporters_count)}</strong>{' '}
                            supporters
                        </span>
                    </p>
                    <div className="flex flex-col gap-6">
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                            {row.action === WorkflowActionTypeEnum.SEND_SMS && (
                                <ActionRowMetricContainer>
                                    <ActionRowMetricIcon
                                        icon={faMobile}
                                        className="text-green-500"
                                    />
                                    <ActionRowMetricValueContainer>
                                        <ActionRowMetricValue className="text-green-500">
                                            {row.message.delivery_rate}%
                                            <ActionRowMetricValueCount>
                                                ({row.message.delivery_count})
                                            </ActionRowMetricValueCount>
                                        </ActionRowMetricValue>
                                        <ActionRowMetricLabel>delivered</ActionRowMetricLabel>
                                    </ActionRowMetricValueContainer>
                                </ActionRowMetricContainer>
                            )}

                            {row.action === WorkflowActionTypeEnum.SEND_EMAIL && (
                                <>
                                    <ActionRowMetricContainer>
                                        <ActionRowMetricIcon
                                            icon={faEnvelopeOpen}
                                            className="text-green-500"
                                        />
                                        <ActionRowMetricValueContainer>
                                            <ActionRowMetricValue className="text-green-500">
                                                {row.message.open_rate}%
                                                <ActionRowMetricValueCount>
                                                    ({row.message.unique_open_count})
                                                </ActionRowMetricValueCount>
                                            </ActionRowMetricValue>
                                            <ActionRowMetricLabel>opened</ActionRowMetricLabel>
                                        </ActionRowMetricValueContainer>
                                    </ActionRowMetricContainer>
                                    <ActionRowMetricContainer>
                                        <ActionRowMetricIcon
                                            icon={faXmarkToSlot}
                                            className="text-orange-600"
                                        />
                                        <ActionRowMetricValueContainer>
                                            <ActionRowMetricValue className="text-orange-600">
                                                {row.message.bounce_rate}%
                                                <ActionRowMetricValueCount>
                                                    ({row.message.bounce_count})
                                                </ActionRowMetricValueCount>
                                            </ActionRowMetricValue>
                                            <ActionRowMetricLabel>bounced</ActionRowMetricLabel>
                                        </ActionRowMetricValueContainer>
                                    </ActionRowMetricContainer>
                                </>
                            )}

                            <ActionRowMetricContainer>
                                <ActionRowMetricIcon
                                    icon={faXmarkToSlot}
                                    className="text-red-600"
                                />
                                <ActionRowMetricValueContainer>
                                    <ActionRowMetricValue className="text-red-600">
                                        {row.message.unsubscribe_rate}%
                                        <ActionRowMetricValueCount>
                                            ({row.message.unsubscribe_rate})
                                        </ActionRowMetricValueCount>
                                    </ActionRowMetricValue>
                                    <ActionRowMetricLabel>unsubscribed</ActionRowMetricLabel>
                                </ActionRowMetricValueContainer>
                            </ActionRowMetricContainer>
                        </div>
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                            <ActionRowMetricContainer>
                                <ActionRowMetricIcon
                                    icon={faArrowPointer}
                                    className="text-blue-600"
                                />
                                <ActionRowMetricValueContainer>
                                    <ActionRowMetricValue className="text-blue-600">
                                        {row.message.click_rate}%
                                        <ActionRowMetricValueCount>
                                            ({row.message.click_count})
                                        </ActionRowMetricValueCount>
                                    </ActionRowMetricValue>
                                    <ActionRowMetricLabel>clicks</ActionRowMetricLabel>
                                </ActionRowMetricValueContainer>
                            </ActionRowMetricContainer>

                            <ActionRowMetricContainer>
                                <ActionRowMetricIcon
                                    icon={faGift}
                                    className="text-brand-violet-primary"
                                />
                                <ActionRowMetricValueContainer>
                                    <ActionRowMetricValue className="text-brand-violet-primary">
                                        <CurrencySymbol />
                                        {maskDecimal(row.message.revenue)}
                                        <ActionRowMetricValueCount>
                                            ({row.message.donation_supporters_count})
                                        </ActionRowMetricValueCount>
                                    </ActionRowMetricValue>
                                    <ActionRowMetricLabel>revenue generated</ActionRowMetricLabel>
                                </ActionRowMetricValueContainer>
                            </ActionRowMetricContainer>
                        </div>
                    </div>
                </div>
            </Collapsible.Content>
        );
    },
);

interface ActionRowMetricContainerProps extends ComponentProps<'div'> {}
const ActionRowMetricContainer = ({
    className,
    children,
    ...rest
}: ActionRowMetricContainerProps) => {
    return (
        <div className={twMerge('flex min-w-48 items-center gap-3', className)} {...rest}>
            {children}
        </div>
    );
};

interface ActionRowMetricIconProps extends FontAwesomeIconProps {}
const ActionRowMetricIcon = ({ icon, className, ...rest }: ActionRowMetricIconProps) => {
    return (
        <div className="flex size-12 items-center justify-center rounded-xl border border-gray-200 bg-white shadow-sm">
            <FontAwesomeIcon icon={icon} className={className} size="lg" {...rest} />
        </div>
    );
};

interface ActionRowMetricValueContainerProps extends ComponentProps<'div'> {}
const ActionRowMetricValueContainer = ({
    className,
    children,
    ...rest
}: ActionRowMetricValueContainerProps) => {
    return (
        <div className={twMerge('flex flex-col', className)} {...rest}>
            {children}
        </div>
    );
};

interface ActionRowMetricValueProps extends ComponentProps<'p'> {}
const ActionRowMetricValue = ({ className, children, ...rest }: ActionRowMetricValueProps) => {
    return (
        <p className={twMerge('flex items-center gap-2 text-lg font-medium', className)} {...rest}>
            {children}
        </p>
    );
};

interface ActionRowMetricValueProps extends ComponentProps<'p'> {}
const ActionRowMetricValueCount = ({ className, children, ...rest }: ActionRowMetricValueProps) => {
    return (
        <span className={twMerge('text-sm font-normal text-gray-500', className)} {...rest}>
            {children}
        </span>
    );
};

interface ActionRowMetricLabelProps extends ComponentProps<'p'> {}
const ActionRowMetricLabel = ({ className, children, ...rest }: ActionRowMetricLabelProps) => {
    return (
        <p className={twMerge('text-sm font-medium text-gray-600', className)} {...rest}>
            {children}
        </p>
    );
};

const Root = ActionMetricsRowRoot;
const Title = ActionMetricsRowTitle;
const Description = ActionMetricsRowDescription;
const CollapsibleTrigger = ActionMetricsRowCollapsibleTrigger;
const Content = ActionMetricsRowContent;

export {
    Root,
    Title,
    Description,
    CollapsibleTrigger,
    Content,
    //
    ActionMetricsRowRoot,
    ActionMetricsRowTitle,
    ActionMetricsRowDescription,
    ActionMetricsRowCollapsibleTrigger,
    ActionMetricsRowContent,
};
